import React, { useContext, useState, useEffect, useCallback, useRef } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { theme } from '../theme';
import SimulationModal from '../components/SimulationModal';
import Navbar from '../components/Navbar';
import { sidebarStyles } from './admin/components/adminStyles';
import { HomeIcon, MapPinIcon, SlidersIcon, ChartBarIcon, UsersIcon } from './admin/components/navIcons';
import FieldManagement from './farmer/components/FieldManagement';
import WelcomeView from './farmer/components/WelcomeView';
import AuctionBiddingView from './farmer/components/AuctionBiddingView';
import AuctionStatusView from './farmer/components/AuctionStatusView';
import ProfileView from './farmer/components/ProfileView';
import { styles, ODDS_BADGE_COLORS } from './farmer/components/dashboardStyles';

const NAV_ITEMS = [
  { key: 'welcome', label: 'Welcome', icon: <HomeIcon /> },
  { key: 'fields', label: 'My Fields', icon: <MapPinIcon /> },
  { key: 'dashboard', label: 'Manage Bids', icon: <SlidersIcon /> },
  { key: 'status', label: 'Activity overview', icon: <ChartBarIcon /> },
  { key: 'profile', label: 'My Details', icon: <UsersIcon /> },
];

const SidebarLink = ({ icon, label, active, collapsed, onClick }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={collapsed ? label : undefined}
      style={{
        ...sidebarStyles.link,
        ...(active ? sidebarStyles.linkActive : (hovered ? sidebarStyles.linkHover : {})),
        justifyContent: collapsed ? 'center' : 'flex-start'
      }}
    >
      <span style={sidebarStyles.linkIcon}>{icon}</span>
      {!collapsed && (
        <span style={sidebarStyles.linkTextCol}>
          <span style={sidebarStyles.linkLabel}>{label}</span>
        </span>
      )}
    </button>
  );
};

const FARM_TYPES = [
    "Arable - Cereals and Oilseeds",
    "Arable - General Cropping",
    "Dairy",
    "Lowland Grazing",
    "Mixed",
    "Horticulture",
    "Pigs & Poultry",
    "Nature"
];

// When exactly one allowed action of the relevant type (Field vs Whole Farm)
// exists for the active event, pre-select it instead of forcing the farmer
// to pick from a dropdown with a single option.
const getSoleActionId = (eventDetails, isWholeFarm) => {
  if (!eventDetails) return '';
  const matches = eventDetails.allowedActions.filter(a => isWholeFarm ? a.isWholeFarm : !a.isWholeFarm);
  return matches.length === 1 ? matches[0].actionId : '';
};

const Dashboard = () => {
  const { user, logout, loadUser } = useContext(AuthContext);
  const navigate = useNavigate();

  // --- UI TABS STATE ---
  const [activeTab, setActiveTab] = useState('welcome');
  const [pendingTab, setPendingTab] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // --- DATA STATE ---
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [activeEventDetails, setActiveEventDetails] = useState(null);
  const [selectedParcel, setSelectedParcel] = useState(null);
  const [isWholeFarmBidMode, setIsWholeFarmBidMode] = useState(false);
  const [proposalSavedBadgeVisible, setProposalSavedBadgeVisible] = useState(false);
  const [myBids, setMyBids] = useState([]);
  const [allBids, setAllBids] = useState([]);
  const [focusAreaZones, setFocusAreaZones] = useState([]);

  // --- FIELD MANAGEMENT STATE ---
  const [managedParcels, setManagedParcels] = useState([]);

  // --- ACTION LIBRARY LOOKUP (actionId -> shortDescription, for the Available
  // Actions table in Manage Bids) ---
  const [actionDescriptions, setActionDescriptions] = useState({});
  // actionId -> Action.variations[] (farmer-facing sub-options at bid time,
  // e.g. Seed Mix/Planting Date) — not part of Event.allowedActions, which
  // only snapshots label/unit/price, so this is fetched separately.
  const [actionVariations, setActionVariations] = useState({});
  const [zoomTarget, setZoomTarget] = useState(null);
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [saveEditTrigger, setSaveEditTrigger] = useState(0);
  
  // --- MAP & SBI STATE ---
  const [sbiInput, setSbiInput] = useState('');
  const [activeSbi, setActiveSbi] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [recenterTrigger, setRecenterTrigger] = useState(0);
  const [showConfirmReset, setShowConfirmReset] = useState(false);
  const [manualReset, setManualReset] = useState(false);
  const [sbiLookupFailed, setSbiLookupFailed] = useState(false);
  const autoSbiImportAttempted = useRef(false);

  // --- DRAWING CUSTOM PARCELS STATE ---
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [showNameModal, setShowNameModal] = useState(false);
  const [drawnParcelData, setDrawnParcelData] = useState(null);
  const [customFieldName, setCustomFieldName] = useState('');
  const [customFieldLandUse, setCustomFieldLandUse] = useState('');
  const [editingParcelId, setEditingParcelId] = useState(null); 
  
  // --- INLINE RENAMING STATE ---
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameInput, setRenameInput] = useState('');

  // --- UI STATE ---
  const [loadingMsg, setLoadingMsg] = useState('');
  const [bidsLoading, setBidsLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [simModalOpen, setSimModalOpen] = useState(false);
  const [simEventData, setSimEventData] = useState(null);
  const [showTermsModal, setShowTermsModal] = useState(false);

  // --- "CHECK YOUR ODDS" TRACKING (session-only; bidId -> { status, checkedAt }) ---
  const [oddsMap, setOddsMap] = useState({});

  // --- BID FORM STATE ---
  const [selectedAction, setSelectedAction] = useState('');
  const [bidAmount, setBidAmount] = useState('');
  const [bidUnits, setBidUnits] = useState('');
  const [selectedVariations, setSelectedVariations] = useState({});
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // --- TYPE 4 (GRANTS - % CONTRIBUTION) BID FORM STATE ---
  const [totalActionCost, setTotalActionCost] = useState('');
  const [priceEvidenceFile, setPriceEvidenceFile] = useState(null);

  // --- WHOLE FARM PROPOSAL FORM STATE (Manual Review auctions) ---
  const [isProposalModalOpen, setIsProposalModalOpen] = useState(false);
  const [proposalForm, setProposalForm] = useState({
    proposalOverview: '', bidPrice: '',
    impactStatement: '', evaluationStatement: ''
  });
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [newAttachmentFiles, setNewAttachmentFiles] = useState([]);

  // --- FILTER STATE (STATUS TAB) ---
  const [statusFilterName, setStatusFilterName] = useState('');
  const [statusFilterState, setStatusFilterState] = useState('all'); 

  // --- PROFILE FORM STATE ---
  const [profileData, setProfileData] = useState({
    firstName: '', surname: '', phoneNumber: '',
    addressLine1: '', addressLine2: '', town: '', county: '', postcode: '',
    businessName: '', mainSBI: '', farmType: FARM_TYPES[0],
    secondFarmName: '', secondSBI: '', secondFarmType: FARM_TYPES[0],
    thirdFarmName: '', thirdSBI: '', thirdFarmType: FARM_TYPES[0]
  });
  const [profileMsg, setProfileMsg] = useState('');

  // ------------------------------------------------------------------
  // 1. INITIAL LOAD & EFFECTS
  // ------------------------------------------------------------------

  useEffect(() => {
    if (user && user.sbi && !activeSbi && !manualReset) {
      setActiveSbi(user.sbi);
      setSbiInput(user.sbi);

      // Pre-load the farm boundaries for the SBI supplied at registration —
      // otherwise the map stays blank until the farmer re-runs the RPA
      // preview/import flow themselves. Guarded by a ref (not just the
      // `!activeSbi` check above) so a `user` object identity change from a
      // background loadUser() refresh can't fire a second import mid-session.
      if (!autoSbiImportAttempted.current) {
        autoSbiImportAttempted.current = true;
        const sbiToImport = user.sbi;
        api.post('/rpa/import', { sbi: sbiToImport })
          .then(() => api.get(`/parcels?sbi=${sbiToImport}&includeIgnored=true&t=${Date.now()}`))
          .then(res => setManagedParcels(res.data))
          .catch(err => {
            console.error('Automatic SBI boundary import failed', err);
            setSbiLookupFailed(true);
          });
      }
    }

    if (user) {
        let fName = user.firstName || '';
        let sName = user.surname || '';
        if (!fName && !sName && user.name) {
            const parts = user.name.split(' ');
            fName = parts[0];
            sName = parts.slice(1).join(' ');
        }

        setProfileData({
            firstName: fName,
            surname: sName,
            phoneNumber: user.phoneNumber || '',
            addressLine1: user.addressLine1 || '',
            addressLine2: user.addressLine2 || '',
            town: user.town || '',
            county: user.county || '',
            postcode: user.postcode || '',
            businessName: user.businessName || '',
            mainSBI: user.mainSBI || '',
            farmType: user.farmType || FARM_TYPES[0],
            secondFarmName: user.secondFarmName || '',
            secondSBI: user.secondSBI || '',
            secondFarmType: user.secondFarmType || FARM_TYPES[0],
            thirdFarmName: user.thirdFarmName || '',
            thirdSBI: user.thirdSBI || '',
            thirdFarmType: user.thirdFarmType || FARM_TYPES[0]
        });
        setIsDirty(false); 
    }
  }, [user, activeSbi, manualReset]);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await api.get(`/events?t=${Date.now()}`);
        const allEvents = res.data.sort((a, b) => {
          const aActive = new Date(a.endDate) > new Date();
          const bActive = new Date(b.endDate) > new Date();
          if (aActive === bActive) return new Date(b.endDate) - new Date(a.endDate);
          return bActive - aActive;
        });
        setEvents(allEvents);
        
        setSelectedEvent(prev => {
            if (allEvents.length > 0 && !prev) return allEvents[0]._id;
            return prev;
        });

      } catch (err) { console.error("Error loading events", err); }
    };
    fetchEvents();
  }, []);

  useEffect(() => {
    const fetchActionDescriptions = async () => {
      try {
        const res = await api.get(`/actions?includeArchived=true&t=${Date.now()}`);
        const descMap = {};
        const variationsMap = {};
        res.data.forEach(a => {
            descMap[a._id] = a.shortDescription || '';
            if (a.variations && a.variations.length > 0) variationsMap[a._id] = a.variations;
        });
        setActionDescriptions(descMap);
        setActionVariations(variationsMap);
      } catch (err) { console.error("Error loading action descriptions", err); }
    };
    fetchActionDescriptions();
  }, []);

  useEffect(() => {
    if (!selectedEvent) return;
    
    if (activeTab === 'dashboard') {
        const fetchBids = async () => {
          setBidsLoading(true);
          try {
            const ev = events.find(e => e._id === selectedEvent);
            if (ev) setActiveEventDetails(ev);
            const res = await api.get(`/bids?eventId=${selectedEvent}&t=${Date.now()}`);
            setMyBids(res.data);

            const zoneRes = await api.get(`/zones/${selectedEvent}`);
            setFocusAreaZones(zoneRes.data.filter(z => z.type === 'priority'));

          } catch (err) { console.error(err); }
          finally { setBidsLoading(false); }
        };
        fetchBids();
    }
  }, [selectedEvent, events, activeTab]);

  useEffect(() => {
    // Not gated to activeTab === 'fields': the Welcome view's subtitle also
    // depends on whether the farmer has any parcels loaded/created yet.
    if (!activeSbi) return;
    const loadFields = async () => {
        try {
            const res = await api.get(`/parcels?sbi=${activeSbi}&includeIgnored=true&t=${Date.now()}`);
            setManagedParcels(res.data);
        } catch (err) { console.error(err); }
    };
    loadFields();
  }, [activeTab, activeSbi]);

  useEffect(() => {
    if (activeTab === 'status') {
        const fetchAllHistory = async () => {
            setLoadingMsg('Loading Bid History...');
            try {
                const res = await api.get(`/bids?t=${Date.now()}`);
                setAllBids(res.data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoadingMsg('');
            }
        };
        fetchAllHistory();
    }
  }, [activeTab]);

  useEffect(() => {
    if (selectedParcel && !isWholeFarmBidMode) {
      const existingBid = myBids.find(b => b.parcel && b.parcel._id === selectedParcel.id);
      if (existingBid) {
        setSelectedAction(existingBid.selectedAction);
        setBidAmount(existingBid.amount);
        setBidUnits(existingBid.units || '');
        setSelectedVariations(existingBid.selectedVariations || {});
      } else {
        setSelectedAction(getSoleActionId(activeEventDetails, false));
        setBidAmount('');
        setBidUnits('');
        setSelectedVariations({});
      }
    } else if (!isWholeFarmBidMode && !selectedParcel) {
      setSelectedAction('');
      setBidAmount('');
      setBidUnits('');
      setSelectedVariations({});
    }
  }, [selectedParcel, myBids, isWholeFarmBidMode, activeEventDetails]);

  const onLogout = () => { logout(); navigate('/login'); };

  // ------------------------------------------------------------------
  // 2. HANDLERS
  // ------------------------------------------------------------------
  const handleParcelSelect = useCallback((p) => {
      setSelectedParcel(p);
      setIsWholeFarmBidMode(false);
      setIsRenaming(false);
  }, []);

  // Field Management's map click handler — stable reference so it doesn't
  // retrigger MapComponent's init effect (see FieldManagement's noop comment).
  const handleFieldMapSelect = useCallback((p) => {
      setSelectedFieldId(p ? p.id : null);
  }, []);

  const handlePreviewFarm = async (e) => {
    e.preventDefault();
    if (!sbiInput) return;
    setLoadingMsg('Contacting RPA Government Gateway...');
    try {
      const res = await api.post('/rpa/preview', { sbi: sbiInput });
      setPreviewData(res.data);
      setLoadingMsg('');
      setSelectedParcel(null);
    } catch (err) { 
      setLoadingMsg(''); 
      alert(err.response?.data?.msg || 'Preview failed'); 
    }
  };

  const handleConfirmFarm = async () => {
    setLoadingMsg('Saving Farm Data...');
    try {
      await api.post('/rpa/import', { sbi: sbiInput });
      setLoadingMsg('');
      setModal({
          title: 'Success!',
          message: 'Farm imported successfully. You can now select fields to bid.',
          confirmText: 'OK',
          onConfirm: () => setModal(null)
      });
      setPreviewData(null); 
      setActiveSbi(sbiInput); 
      setManualReset(false); 
      setSelectedParcel(null);
    } catch (err) {
      setLoadingMsg('');
      alert(err.response?.data?.msg || 'Import failed');
    }
  };

  const handleCancelPreview = () => {
    setPreviewData(null);
    if (!activeSbi) setSbiInput(''); 
    setSelectedParcel(null);
  };

  const requestChangeSbi = () => { setShowConfirmReset(true); };

  const confirmChangeSbi = () => {
      setManualReset(true); 
      setActiveSbi('');
      setSbiInput('');
      setSelectedParcel(null);
      setPreviewData(null);
      setShowConfirmReset(false);
  };

  const handleRecenter = () => { setRecenterTrigger(prev => prev + 1); };

  const handleRenameSubmit = async (e) => {
      e.preventDefault();
      setLoadingMsg('Renaming field...');
      try {
          await api.put(`/parcels/${selectedParcel.id}`, { customName: renameInput });
          setSelectedParcel(prev => ({ ...prev, customName: renameInput }));
          setRecenterTrigger(prev => prev + 1);
      } catch (err) {
          alert('Failed to rename field. Please try again.');
      } finally {
          setLoadingMsg('');
          setIsRenaming(false);
      }
  };

  const toggleDrawingMode = () => {
    setIsDrawingMode(!isDrawingMode);
    setSelectedParcel(null); 
  };

  const handleDrawComplete = (geometryData) => {
    setIsDrawingMode(false);
    setDrawnParcelData(geometryData);
    setCustomFieldName('');
    setCustomFieldLandUse('');
    setShowNameModal(true);
  };

  const saveCustomParcel = async () => {
    if (!customFieldName.trim()) return alert("Please enter a name for the field.");
    if (!customFieldLandUse) return alert("Please select a land use for this field.");
    setLoadingMsg('Saving Custom Field...');
    setShowNameModal(false);

    try {
      await api.post('/parcels', {
        sbi: activeSbi,
        name: customFieldName,
        landUse: customFieldLandUse,
        geometry: drawnParcelData.rings,
        area: drawnParcelData.area,
        perimeter: drawnParcelData.perimeter
      });

      setLoadingMsg('');
      setRecenterTrigger(prev => prev + 1);
      await loadManagedParcels();
      setModal({
        title: 'Success!',
        message: 'Custom field saved. You can now select it to place bids.',
        confirmText: 'OK',
        onConfirm: () => setModal(null)
      });
    } catch (err) {
      setLoadingMsg('');
      alert(err.response?.data?.msg || 'Failed to save custom field');
    }
  };

  const loadManagedParcels = async () => {
    if (!activeSbi) return;
    try {
        const res = await api.get(`/parcels?sbi=${activeSbi}&includeIgnored=true&t=${Date.now()}`);
        setManagedParcels(res.data);
    } catch (err) { console.error(err); }
  };

  const handleStartWithoutSbi = () => {
    const pseudoSbi = `LOCAL_${user._id.slice(-6).toUpperCase()}`;
    setActiveSbi(pseudoSbi);
    setSbiInput(pseudoSbi);
    setManualReset(false);
  };

  const handleFieldRename = async (parcelId, newName) => {
    try {
        await api.put(`/parcels/${parcelId}`, { customName: newName });
        await loadManagedParcels();
    } catch (err) {
        alert('Failed to rename field. Please try again.');
    }
  };

  const handleToggleIgnored = async (parcelId, nextValue) => {
    try {
        await api.put(`/parcels/${parcelId}`, { isIgnored: nextValue });
        await loadManagedParcels();
    } catch (err) {
        alert('Failed to update field visibility.');
    }
  };

  const handleToggleIgnoredBulk = async (parcelIds, nextValue) => {
    try {
        const res = await api.put('/parcels/bulk-ignore', { parcelIds, isIgnored: nextValue });
        await loadManagedParcels();
        if (res.data?.count === 0) {
            alert('No fields were updated — you may not have permission to modify these fields.');
        } else if (res.data?.count < parcelIds.length) {
            alert(`Updated ${res.data.count} of ${parcelIds.length} fields — the rest could not be modified.`);
        }
    } catch (err) {
        console.error('Bulk ignore toggle failed:', err);
        alert(err.response?.data?.msg || 'Failed to update field visibility.');
    }
  };

  const handleZoomToField = (parcelId) => {
    setZoomTarget({ id: parcelId, nonce: Date.now() });
  };

  const handleEditShape = (parcelId) => {
    setEditingParcelId(parcelId);
  };

  // Finalizes the in-progress reshape (MapComponent's sketch "update" event
  // then fires and calls handleEditComplete below to persist it).
  const handleSaveEditShape = () => {
    setSaveEditTrigger(prev => prev + 1);
  };

  // Discards the in-progress reshape without saving — MapComponent's own
  // editingParcelId effect handles restoring the original shape and visibility.
  const handleCancelEditShape = () => {
    setEditingParcelId(null);
  };

  const handleEditComplete = async (updatedGeometryData) => {
    const parcelId = editingParcelId;
    setEditingParcelId(null);
    setLoadingMsg('Updating Custom Field...');

    try {
      await api.put(`/parcels/${parcelId}`, {
        geometry: updatedGeometryData.rings,
        area: updatedGeometryData.area,
        perimeter: updatedGeometryData.perimeter
      });

      setLoadingMsg('');
      setRecenterTrigger(prev => prev + 1);
      await loadManagedParcels();
    } catch (err) {
      setLoadingMsg('');
      alert('Failed to update field shape');
    }
  };

  const handleDeleteCustomField = (parcelId) => {
    setModal({
        title: 'Delete Custom Field?',
        message: 'WARNING: This will delete this field AND withdraw any bids you have placed on it across ALL events. This action cannot be undone.\n\nAre you sure?',
        confirmText: 'Yes, Delete Everything',
        cancelText: 'Cancel',
        onConfirm: async () => {
            setModal(null);
            setLoadingMsg('Deleting field and bids...');
            try {
                await api.delete(`/parcels/${parcelId}`);
                setLoadingMsg('');
                if (selectedParcel?.id === parcelId) setSelectedParcel(null);
                setRecenterTrigger(prev => prev + 1);
                await loadManagedParcels();
                if (selectedEvent) {
                    const res = await api.get(`/bids?eventId=${selectedEvent}&t=${Date.now()}`);
                    setMyBids(res.data);
                }
            } catch(err) {
                setLoadingMsg('');
                alert('Failed to delete custom field');
            }
        }
    });
  };

  const executeSaveBid = async (payload) => {
      setLoadingMsg('Saving draft bid...Review & Submit to finalise');
      try {
        await api.post('/bids', payload);
        if (activeTab === 'dashboard') {
            const res = await api.get(`/bids?eventId=${selectedEvent}&t=${Date.now()}`);
            setMyBids(res.data);
        } else {
            const res = await api.get(`/bids?t=${Date.now()}`);
            setAllBids(res.data);
        }

        setSelectedParcel(null);
        setIsWholeFarmBidMode(false);
        setBidAmount('');
        setBidUnits('');
        setSelectedVariations({});
        setTotalActionCost('');
        setPriceEvidenceFile(null);
        setModal(null);
        setLoadingMsg('');

      } catch (err) {
        setLoadingMsg('');
        // --- GRANT WHOLE-FARM PAYMENT CAP GATE ---
        // Mirrors the non-grant "Payment Cap Exceeded" gate in handleSaveDraft
        // below, except the grant price isn't known until the server computes
        // it — so the server bounces the first attempt with 409 instead of the
        // client pre-checking bidAmount, and this is where that choice surfaces.
        // Confirming resubmits the same payload with acceptGrantCap so the
        // server clamps to the cap instead of bouncing again.
        if (err.response?.status === 409 && err.response?.data?.requiresCapConfirmation) {
            const { cappedAmount } = err.response.data;
            setModal({
                title: 'Payment Cap Exceeded',
                message: `This grant exceeds the maximum payment per farm limit for this action. Would you like to cancel bid or accept the capped amount? (£${cappedAmount.toLocaleString('en-GB')})`,
                confirmText: 'Accept Capped Amount',
                cancelText: 'Cancel Bid',
                onConfirm: () => { setModal(null); executeSaveBid({ ...payload, acceptGrantCap: true }); }
            });
            return;
        }
        alert(err.response?.data?.msg || 'Submission failed');
      }
  };

  const handleSaveDraft = async (e) => {
    e.preventDefault();
    if (!selectedEvent) return alert("No event selected!");
    
    if (activeEventDetails) {
        const isClosed = activeEventDetails.status === 'closed' || new Date(activeEventDetails.endDate) < new Date();
        if (isClosed) return alert("This event has ended. No new bids allowed.");
    }

    const isGrant = activeEventDetails?.auctionType === 'grants' || activeEventDetails?.auctionType === 'grants_fixed';
    const isGrantsContribution = activeEventDetails?.auctionType === 'grants_contribution';
    if (!isGrant && (!bidAmount || bidAmount <= 0)) {
        return alert("Please enter a valid price.");
    }

    if (!isWholeFarmBidMode && (!selectedParcel || !selectedParcel.id || selectedParcel.id.startsWith('preview-'))) {
       return alert("Please 'Import' the farm first.");
    }

    // 👇 SURGICAL FIX: Force input capture for Flex Bids
    const actionDef = activeEventDetails?.allowedActions.find(a => a.actionId === selectedAction);
    const requiresUnits = actionDef && actionDef.unit !== 'farm' && (isWholeFarmBidMode || actionDef.unit !== 'ha');

    let finalUnits = 0;
    if (requiresUnits) {
        if (!bidUnits || bidUnits <= 0) return alert(`Please enter the quantity in ${actionDef.unit}.`);
        finalUnits = parseFloat(bidUnits);
    }

    if (isGrantsContribution) {
        if (!totalActionCost || totalActionCost <= 0) return alert("Please enter the Total Action Cost.");
        const maxContributionPercent = actionDef?.maxContributionPercent ?? 100;
        const contributionPercent = (parseFloat(bidAmount) / parseFloat(totalActionCost)) * 100;
        if (contributionPercent > maxContributionPercent) {
            return alert(`Bid price cannot exceed ${maxContributionPercent}% of the Total Action Cost.`);
        }
    }

    // --- WHOLE-FARM PAYMENT CAP GATE ---
    // Whole Farm (non-manual) actions may carry a per-farm payment ceiling
    // (allowedActions[].maxPaymentPerFarm) — enforced server-side during
    // allocation regardless, but gating here lets the farmer choose upfront
    // to accept the capped amount rather than discovering the cap only after
    // losing to it. Manual (EOI) events are exempt — their whole-farm
    // proposals go through a separate review workflow, not this form.
    // actionDef here is an Event.allowedActions[] snapshot (actionId/label/
    // budget/isWholeFarm/...), not the full Action library document — it has
    // no actionType field, only the isWholeFarm boolean copy.
    const isWholeFarmAction = isWholeFarmBidMode && !!actionDef?.isWholeFarm;
    const farmCap = actionDef?.maxPaymentPerFarm;
    if (!isGrant && isWholeFarmAction && activeEventDetails?.auctionType !== 'manual' && farmCap && parseInt(bidAmount) > farmCap) {
        setModal({
            title: 'Payment Cap Exceeded',
            message: `Bid exceeds the maximum payment per farm limit for this action. Would you like to cancel bid or accept the capped amount? (£${farmCap.toLocaleString('en-GB')})`,
            confirmText: 'Accept Capped Amount',
            cancelText: 'Cancel Bid',
            onConfirm: () => {
                setModal(null);
                setBidAmount(String(farmCap));
                submitBid(farmCap, finalUnits, actionDef, isGrant, isGrantsContribution);
            }
        });
        return;
    }

    submitBid(isGrant ? 0 : parseInt(bidAmount), finalUnits, actionDef, isGrant, isGrantsContribution);
  };

  // Builds the bid payload and either submits directly or, if a non-draft
  // bid already exists for this field/action, gates on an overwrite
  // confirmation first. Split out of handleSaveDraft so the whole-farm
  // payment-cap gate above can call it a second time, with the capped
  // amount, after the farmer accepts.
  const submitBid = (finalAmount, finalUnits, actionDef, isGrant, isGrantsContribution) => {
    let payload;
    if (isGrantsContribution) {
        payload = new FormData();
        payload.append('parcelId', isWholeFarmBidMode ? '' : selectedParcel.id);
        payload.append('eventId', selectedEvent);
        payload.append('action', selectedAction);
        payload.append('amount', finalAmount);
        payload.append('units', finalUnits);
        payload.append('totalActionCost', totalActionCost);
        payload.append('selectedVariations', JSON.stringify(selectedVariations));
        if (priceEvidenceFile) payload.append('priceEvidence', priceEvidenceFile);
    } else {
        payload = {
            parcelId: isWholeFarmBidMode ? null : selectedParcel.id,
            eventId: selectedEvent,
            action: selectedAction,
            amount: finalAmount,
            units: finalUnits,
            selectedVariations
        };
    }

    const existingBid = isWholeFarmBidMode
        ? myBids.find(b => b.selectedAction === selectedAction && b.status !== 'draft')
        : myBids.find(b => b.parcel && b.parcel._id === selectedParcel.id && b.status !== 'draft');

    if (existingBid) {
        setModal({
            title: 'Overwrite Existing Bid?',
            message: `You have already submitted a bid for this ${isWholeFarmBidMode ? 'action' : 'field'}.\n\nDo you want to overwrite it with this new draft?`,
            confirmText: 'Yes, Overwrite',
            cancelText: 'Cancel',
            // Close this modal first — it renders above the loading overlay
            // (zIndex 10000 vs 9999), so leaving it open would hide the
            // "Revise Bid" spinner behind it for the whole request.
            onConfirm: () => { setModal(null); executeSaveBid(payload); }
        });
    } else {
        executeSaveBid(payload);
    }
  };

  const resetProposalForm = () => {
    setSelectedAction(getSoleActionId(activeEventDetails, true));
    setProposalForm({
      proposalOverview: '', bidPrice: '',
      impactStatement: '', evaluationStatement: ''
    });
    setExistingAttachments([]);
    setNewAttachmentFiles([]);
  };

  const handleOpenNewProposal = () => {
    resetProposalForm();
    setIsProposalModalOpen(true);
  };

  const handleSaveProposalDraft = async (e) => {
    e.preventDefault();
    if (!selectedEvent) return alert("No event selected!");
    if (!selectedAction) return alert("Please select an action.");

    if (activeEventDetails) {
        const isClosed = activeEventDetails.status === 'closed' || new Date(activeEventDetails.endDate) < new Date();
        if (isClosed) return alert("This event has ended. No new bids allowed.");
    }

    const currentActionDef = activeEventDetails?.allowedActions?.find(a => a.actionId === selectedAction);
    if (!currentActionDef?.fundingOptional && (!proposalForm.bidPrice || proposalForm.bidPrice <= 0)) {
        return alert("Please enter the value of grant requested.");
    }

    const formData = new FormData();
    formData.append('eventId', selectedEvent);
    formData.append('action', selectedAction);
    formData.append('status', 'draft');
    formData.append('attachmentsTouched', 'true');
    formData.append('proposalOverview', proposalForm.proposalOverview);
    formData.append('bidPrice', proposalForm.bidPrice);
    formData.append('impactStatement', proposalForm.impactStatement);
    formData.append('evaluationStatement', proposalForm.evaluationStatement);
    existingAttachments.forEach(a => formData.append('keepAttachmentIds', a._id));
    newAttachmentFiles.forEach(f => formData.append('attachments', f));

    setLoadingMsg('Submitting proposal...');
    try {
        await api.post('/bids', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        const res = await api.get(`/bids?eventId=${selectedEvent}&t=${Date.now()}`);
        setMyBids(res.data);
        setIsProposalModalOpen(false);
        resetProposalForm();
        setProposalSavedBadgeVisible(true);
    } catch (err) {
        alert(err.response?.data?.msg || 'Submission failed');
    } finally {
        setLoadingMsg('');
    }
  };

  const handleEditBid = (bid) => {
    if (activeTab === 'status') {
        setActiveTab('dashboard');
        setSelectedEvent(bid.event);
    }

    const bidEvent = events.find(e => e._id === bid.event);
    if (bidEvent?.auctionType === 'manual') {
        setSelectedParcel(null);
        setIsWholeFarmBidMode(false);
        setSelectedAction(bid.selectedAction);
        setProposalForm({
            proposalOverview: bid.proposalOverview || '',
            bidPrice: bid.bidPrice ?? bid.amount ?? '',
            impactStatement: bid.impactStatement || '',
            evaluationStatement: bid.evaluationStatement || ''
        });
        setExistingAttachments(bid.attachments || []);
        setNewAttachmentFiles([]);
        setIsProposalModalOpen(true);
        return;
    }

    if (!bid.parcel) {
        setSelectedParcel(null);
        setIsWholeFarmBidMode(true);
    } else {
        setIsWholeFarmBidMode(false);
        setSelectedParcel({ 
            id: bid.parcel._id, 
            name: bid.parcel.name, 
            parcelRef: bid.parcel.parcelRef, 
            area: bid.parcel.area || 'Unknown',
            customName: bid.parcel.customName || ''
        });
    }

    setSelectedAction(bid.selectedAction);
    setBidAmount(bid.amount);
    setBidUnits(bid.units || '');
    setSelectedVariations(bid.selectedVariations || {});
  };

  const handleDeleteBid = (bidId) => {
    setModal({
        title: 'Delete Draft',
        message: 'Are you sure you want to delete this draft offer?',
        confirmText: 'Delete',
        cancelText: 'Cancel',
        onConfirm: async () => {
            try { 
              await api.delete(`/bids/${bidId}`); 
              if(activeTab === 'dashboard') {
                  const res = await api.get(`/bids?eventId=${selectedEvent}&t=${Date.now()}`);
                  setMyBids(res.data);
              } else {
                  const res = await api.get(`/bids?t=${Date.now()}`);
                  setAllBids(res.data);
              }
              setModal(null);
            } catch (err) { alert("Failed to delete"); }
        }
    });
  };

  const handleWithdrawBid = (bid) => {
    const readableAction = getActionLabel(bid.selectedAction);
    setModal({
        title: 'Withdraw Bid Review',
        message: `Field: ${bid.parcel ? bid.parcel.parcelRef : 'Whole Farm'}\nAction: ${readableAction}\nAmount: £${bid.amount}\n\nAre you sure you want to withdraw this bid?`,
        confirmText: 'Confirm Withdraw',
        cancelText: 'Keep Bid',
        onConfirm: async () => {
            try {
              await api.post(`/bids/withdraw/${bid._id}`);
              if (activeTab === 'dashboard') {
                  const res = await api.get(`/bids?eventId=${selectedEvent}&t=${Date.now()}`);
                  setMyBids(res.data);
              } else {
                  const res = await api.get(`/bids?t=${Date.now()}`);
                  setAllBids(res.data);
              }
              setModal(null);
            } catch (err) { alert(err.response?.data?.msg || "Failed to withdraw"); }
        }
    });
  };

  const handleCommitEventBids = async (eventIdToCommit) => {
    setLoadingMsg('Submitting your bids...');
    try {
      await api.post('/bids/commit', { eventId: eventIdToCommit });
      setLoadingMsg('');
      setModal({
          title: 'Application Submitted!',
          message: 'We have received your bids and sent a confirmation email to your inbox.',
          confirmText: 'Great!',
          onConfirm: () => {
              setModal(null);
              if (activeTab === 'status') {
                  api.get(`/bids?t=${Date.now()}`).then(res => setAllBids(res.data));
              } else {
                  api.get(`/bids?eventId=${selectedEvent}&t=${Date.now()}`).then(res => setMyBids(res.data));
              }
          }
      });
      setAcceptedTerms(false);
    } catch (err) {
      setLoadingMsg('');
      alert(err.response?.data?.msg || "Commit failed");
    }
  };

  const handleCommitBids = async () => {
    if (!acceptedTerms) return alert("Please accept Terms & Conditions.");
    handleCommitEventBids(selectedEvent);
  };

  // Called by SimulationModal after a "Check Your Odds" run completes, so the
  // bid tables can show a badge/timestamp without re-running the simulation.
  const handleSimulationResults = (results) => {
    const checkedAt = Date.now();
    setOddsMap(prev => {
      const next = { ...prev };
      results.forEach(r => { next[r.id] = { status: r.status, checkedAt }; });
      return next;
    });
  };

  // -- Profile Handlers --
  const handleProfileChange = (e) => {
    setProfileData({ ...profileData, [e.target.name]: e.target.value });
    setIsDirty(true);
  };

  const saveProfileChanges = async () => {
    try {
        const payload = { ...profileData, name: `${profileData.firstName} ${profileData.surname}`.trim() };
        await api.put('/auth/profile', payload);
        await loadUser();
        setProfileMsg('✅ Profile Saved!');
        setIsDirty(false);
        setTimeout(() => setProfileMsg(''), 3000);
        return true;
    } catch (err) {
        setProfileMsg('❌ Error updating profile');
        return false;
    }
  };

  const discardProfileChanges = () => {
     setIsDirty(false);
     if (pendingTab) {
         goToTab(pendingTab);
         setPendingTab(null);
     }
     setModal(null);
     loadUser();
  };

  // The registration-time auto-import (see the `sbi` effect above) sets
  // activeSbi optimistically before knowing whether the import succeeds, so
  // a failed import (e.g. SBI already owned by another account) still left
  // My Fields pointed at that SBI — showing whatever's already stored under
  // it rather than the farmer's own blank slate. Landing on My Fields after
  // that failure clears the bad SBI (locally and on the user's record) so
  // the farmer starts clean, centred on their postcode, and re-enters it.
  const goToTab = async (newTab) => {
      if (newTab === 'fields' && sbiLookupFailed) {
          setSbiLookupFailed(false);
          setManualReset(true);
          setActiveSbi('');
          setSbiInput('');
          setManagedParcels([]);
          try {
              await api.put('/auth/profile', { sbi: '' });
              await loadUser();
          } catch (err) {
              console.error('Failed to clear invalid SBI from profile', err);
          }
      }
      setActiveTab(newTab);
  };

  const handleTabRequest = (newTab) => {
      if (activeTab === 'profile' && isDirty) {
          setPendingTab(newTab);
          setModal({
              title: 'Unsaved Changes',
              message: 'You have unsaved changes in your profile. Do you want to save them before leaving?',
              confirmText: 'Save & Leave',
              cancelText: 'Discard Changes',
              onConfirm: async () => {
                  const success = await saveProfileChanges();
                  if (success) {
                      await goToTab(newTab);
                      setPendingTab(null);
                      setModal(null);
                  }
              },
              onCancelCustom: discardProfileChanges
          });
      } else {
          goToTab(newTab);
      }
  };

  const handleProfileKeyDown = (e) => {
      if (e.key === 'Enter') {
          e.preventDefault();
          setModal({
            title: 'Save Changes?',
            message: 'Do you want to save your profile changes?',
            confirmText: 'Save',
            cancelText: 'Keep Editing',
            onConfirm: async () => {
                await saveProfileChanges();
                setModal(null);
            },
            onCancelCustom: () => setModal(null)
          });
      }
  };

  // ------------------------------------------------------------------
  // 3. HELPERS
  // ------------------------------------------------------------------
  const getActionLabel = (actionId) => {
      if (!activeEventDetails) return actionId;
      const action = activeEventDetails.allowedActions.find(a => a.actionId === actionId);
      return action ? action.label : actionId;
  };
  
  const getStatusLabel = (status) => {
      switch(status) {
          case 'accepted': return 'Winning Bid';
          case 'rejected': return 'Losing Bid';
          case 'explore':  return 'Explore / Under Offline Negotiation';
          case 'pending':  return 'Bid Submitted';
          case 'draft':    return 'Draft';
          default:         return status ? (status.charAt(0).toUpperCase() + status.slice(1)) : 'No Bid Yet';
      }
  };

  const getBidStatusColor = (status) => {
      switch(status) {
          case 'accepted': return '#F59E0B';
          case 'rejected': return '#475569';
          case 'explore':  return theme.colors.accent;
          case 'pending':  return '#163321';
          case 'draft':    return 'rgba(22, 51, 33, 0.5)';
          default:         return '#0D9488';
      }
  };

  const formatCheckedAt = (timestamp) => {
      const diffMins = Math.round((Date.now() - timestamp) / 60000);
      if (diffMins < 1) return 'Checked just now';
      if (diffMins < 60) return `Checked ${diffMins} min${diffMins === 1 ? '' : 's'} ago`;

      const checkedDate = new Date(timestamp);
      const isToday = checkedDate.toDateString() === new Date().toDateString();
      const timeStr = checkedDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

      if (isToday) return `Checked today at ${timeStr}`;
      return `Checked on ${checkedDate.toLocaleDateString('en-GB')}`;
  };

  const getOddsInfo = (bidId) => {
      const entry = oddsMap[bidId];
      if (!entry) return { ...ODDS_BADGE_COLORS.notChecked, checkedAtLabel: null };
      const colors = ODDS_BADGE_COLORS[entry.status] || ODDS_BADGE_COLORS.notChecked;
      return { ...colors, checkedAtLabel: formatCheckedAt(entry.checkedAt) };
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: theme.colors.background }}>
      {loadingMsg && <div style={styles.loaderOverlay}><h2 style={{color: theme.colors.primary}}>⏳ {loadingMsg}</h2></div>}

      {/* MODAL */}
      {/* zIndex bumped above the default 10000 so this can render on top of the
          Batch Review Modal (AuctionBiddingView) when a delete is confirmed from within it. */}
      {modal && (
        <div style={{...styles.modalOverlay, zIndex: 10100}}>
            <div style={styles.modalBox}>
                <h3 style={{marginTop:0, color: theme.colors.primary}}>{modal.title}</h3>
                <p style={{whiteSpace:'pre-line', color:'#444', lineHeight:'1.5'}}>{modal.message}</p>
                <div style={{display:'flex', justifyContent:'flex-end', gap:'10px', marginTop:'20px'}}>
                    {modal.cancelText && (
                        <button 
                            onClick={modal.onCancelCustom ? modal.onCancelCustom : () => setModal(null)} 
                            style={styles.btnReversed}
                        >
                            {modal.cancelText}
                        </button>
                    )}
                    {modal.onConfirm && (
                        <button onClick={modal.onConfirm} style={modal.confirmText === 'Delete' ? styles.btnDanger : styles.btnPrimary}>
                            {modal.confirmText || 'OK'}
                        </button>
                    )}
                </div>
            </div>
        </div>
      )}

      {/* TERMS AND CONDITIONS MODAL */}
      {/* zIndex bumped above the default 10000 for the same reason as the confirm MODAL above —
          it can be opened from within the Batch Review Modal's T&Cs checkbox link. */}
      {showTermsModal && (
        <div style={{...styles.modalOverlay, zIndex: 10100}}>
            <div style={{...styles.modalBox, maxWidth: '600px'}}>
                <h3 style={{marginTop:0, color: theme.colors.primary}}>Terms & Conditions</h3>
                <div style={{maxHeight: '400px', overflowY: 'auto', padding: '15px', backgroundColor: '#F8FAFC', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.85rem', color: '#475569', whiteSpace: 'pre-wrap', fontFamily: '"Inter", sans-serif'}}>
                    {activeEventDetails?.termsAndConditions || "No specific terms provided for this event."}
                </div>
                <div style={{display:'flex', justifyContent:'flex-end', marginTop:'20px'}}>
                    <button onClick={() => setShowTermsModal(false)} style={styles.btnPrimary}>Close</button>
                </div>
            </div>
        </div>
      )}

      {showNameModal && (
        <div style={styles.modalOverlay}>
            <div style={styles.modalBox}>
                <h3 style={{marginTop:0, color: theme.colors.primary}}>Name Your Custom Field</h3>
                <p style={{fontSize:'0.9rem', ...styles.textMuted}}>Enter a name or reference for this new parcel so you can easily identify it.</p>
                
                <div style={{margin: '15px 0', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px', fontSize: '0.85rem'}}>
                    <strong>Calculated Area:</strong> {drawnParcelData?.area?.toFixed(2)} Ha<br/>
                    <strong>Calculated Perimeter:</strong> {drawnParcelData?.perimeter?.toFixed(1)} m
                </div>

                <input
                    type="text"
                    placeholder="e.g. North West Corner"
                    value={customFieldName}
                    onChange={(e) => setCustomFieldName(e.target.value)}
                    style={styles.input}
                    autoFocus
                />

                <select
                    value={customFieldLandUse}
                    onChange={(e) => setCustomFieldLandUse(e.target.value)}
                    style={styles.select}
                >
                    <option value="">-- Select Land Use --</option>
                    <option value="Arable Land">Arable Land</option>
                    <option value="Permanent Grassland">Permanent Grassland</option>
                    <option value="Woodland">Woodland</option>
                    <option value="Other">Other</option>
                </select>

                <div style={{display:'flex', justifyContent:'flex-end', gap:'10px', marginTop:'20px'}}>
                    <button onClick={() => setShowNameModal(false)} style={styles.btnReversed}>Cancel</button>
                    <button onClick={saveCustomParcel} style={styles.btnPrimary}>Save Field</button>
                </div>
            </div>
        </div>
      )}
      
      {simModalOpen && simEventData && (
          <SimulationModal
              eventId={simEventData.id}
              eventName={simEventData.name}
              onClose={() => setSimModalOpen(false)}
              onResults={handleSimulationResults}
          />
      )}
      
      <Navbar user={user} onLogout={onLogout} pageTitle="" maxWidth="1600px" />

      <div style={{ display: 'flex', alignItems: 'flex-start' }}>

        <aside style={{ ...sidebarStyles.sidebar, width: isSidebarCollapsed ? '68px' : '240px' }}>
          <div style={sidebarStyles.toggleRow}>
            <button
              type="button"
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              style={sidebarStyles.toggleBtn}
              title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {isSidebarCollapsed ? '▶' : '◀'}
            </button>
          </div>

          <nav style={sidebarStyles.nav}>
            {!isSidebarCollapsed && <div style={sidebarStyles.sectionLabel}>Menu</div>}
            {NAV_ITEMS.map(item => (
              <SidebarLink
                key={item.key}
                icon={item.icon}
                label={item.label}
                active={activeTab === item.key}
                collapsed={isSidebarCollapsed}
                onClick={() => handleTabRequest(item.key)}
              />
            ))}
          </nav>
        </aside>

        <main style={sidebarStyles.main}>
          <div style={{...styles.container, backgroundColor: theme.colors.background}}>
            {activeTab === 'welcome' && (
              <WelcomeView
                events={events}
                user={user}
                hasParcels={managedParcels.length > 0}
                setSelectedEvent={setSelectedEvent}
                onTabRequest={handleTabRequest}
                sbiLookupFailed={sbiLookupFailed}
              />
            )}
            {activeTab === 'fields' && (
              <FieldManagement
                user={user}
                activeSbi={activeSbi}
                sbiInput={sbiInput}
                setSbiInput={setSbiInput}
                previewData={previewData}
                showConfirmReset={showConfirmReset}
                isDrawingMode={isDrawingMode}
                recenterTrigger={recenterTrigger}
                managedParcels={managedParcels}
                zoomTarget={zoomTarget}
                editingParcelId={editingParcelId}
                saveEditTrigger={saveEditTrigger}
                selectedFieldId={selectedFieldId}
                onMapParcelSelect={handleFieldMapSelect}
                onPreviewFarm={handlePreviewFarm}
                onConfirmFarm={handleConfirmFarm}
                onCancelPreview={handleCancelPreview}
                onRequestChangeSbi={requestChangeSbi}
                onConfirmChangeSbi={confirmChangeSbi}
                onCancelChangeSbi={() => setShowConfirmReset(false)}
                onStartWithoutSbi={handleStartWithoutSbi}
                onToggleDrawing={toggleDrawingMode}
                onDrawComplete={handleDrawComplete}
                onEditComplete={handleEditComplete}
                onRecenter={handleRecenter}
                onRename={handleFieldRename}
                onToggleIgnored={handleToggleIgnored}
                onToggleIgnoredAll={handleToggleIgnoredBulk}
                onZoom={handleZoomToField}
                onEditShape={handleEditShape}
                onSaveEditShape={handleSaveEditShape}
                onCancelEditShape={handleCancelEditShape}
                onDeleteField={handleDeleteCustomField}
              />
            )}
            {activeTab === 'dashboard' && (
              <AuctionBiddingView
                activeSbi={activeSbi}
                events={events}
                selectedEvent={selectedEvent}
                setSelectedEvent={setSelectedEvent}
                activeEventDetails={activeEventDetails}
                actionDescriptions={actionDescriptions}
                actionVariations={actionVariations}
                selectedParcel={selectedParcel}
                setSelectedParcel={setSelectedParcel}
                isWholeFarmBidMode={isWholeFarmBidMode}
                setIsWholeFarmBidMode={setIsWholeFarmBidMode}
                myBids={myBids}
                focusAreaZones={focusAreaZones}
                bidsLoading={bidsLoading}
                isRenaming={isRenaming}
                setIsRenaming={setIsRenaming}
                renameInput={renameInput}
                setRenameInput={setRenameInput}
                selectedAction={selectedAction}
                setSelectedAction={setSelectedAction}
                bidAmount={bidAmount}
                setBidAmount={setBidAmount}
                bidUnits={bidUnits}
                setBidUnits={setBidUnits}
                selectedVariations={selectedVariations}
                setSelectedVariations={setSelectedVariations}
                totalActionCost={totalActionCost}
                setTotalActionCost={setTotalActionCost}
                priceEvidenceFile={priceEvidenceFile}
                setPriceEvidenceFile={setPriceEvidenceFile}
                acceptedTerms={acceptedTerms}
                setAcceptedTerms={setAcceptedTerms}
                setShowTermsModal={setShowTermsModal}
                recenterTrigger={recenterTrigger}
                user={user}
                setSimEventData={setSimEventData}
                setSimModalOpen={setSimModalOpen}
                handleParcelSelect={handleParcelSelect}
                handleRenameSubmit={handleRenameSubmit}
                handleSaveDraft={handleSaveDraft}
                handleEditBid={handleEditBid}
                handleDeleteBid={handleDeleteBid}
                handleWithdrawBid={handleWithdrawBid}
                handleCommitBids={handleCommitBids}
                onTabRequest={handleTabRequest}
                getActionLabel={getActionLabel}
                getOddsInfo={getOddsInfo}
                isProposalModalOpen={isProposalModalOpen}
                setIsProposalModalOpen={setIsProposalModalOpen}
                proposalForm={proposalForm}
                setProposalForm={setProposalForm}
                existingAttachments={existingAttachments}
                setExistingAttachments={setExistingAttachments}
                newAttachmentFiles={newAttachmentFiles}
                setNewAttachmentFiles={setNewAttachmentFiles}
                handleOpenNewProposal={handleOpenNewProposal}
                handleSaveProposalDraft={handleSaveProposalDraft}
                proposalSavedBadgeVisible={proposalSavedBadgeVisible}
                onDismissProposalSavedBadge={() => setProposalSavedBadgeVisible(false)}
              />
            )}
            {activeTab === 'status' && (
              <AuctionStatusView
                allBids={allBids}
                events={events}
                statusFilterName={statusFilterName}
                setStatusFilterName={setStatusFilterName}
                statusFilterState={statusFilterState}
                setStatusFilterState={setStatusFilterState}
                setSimEventData={setSimEventData}
                setSimModalOpen={setSimModalOpen}
                handleEditBid={handleEditBid}
                handleDeleteBid={handleDeleteBid}
                handleWithdrawBid={handleWithdrawBid}
                getStatusLabel={getStatusLabel}
                getBidStatusColor={getBidStatusColor}
                getOddsInfo={getOddsInfo}
              />
            )}
            {activeTab === 'profile' && (
              <ProfileView
                user={user}
                profileData={profileData}
                isDirty={isDirty}
                profileMsg={profileMsg}
                handleProfileChange={handleProfileChange}
                handleProfileKeyDown={handleProfileKeyDown}
                FARM_TYPES={FARM_TYPES}
              />
            )}
          </div>
        </main>

      </div>
    </div>
  );
};

export default Dashboard;